import { useEffect, useState } from "react";
import { useAppSelector } from "@/redux/store";
import { selectIsAuthenticated } from "@/redux/slices/authSlice";
import { useAuthActions } from "@/lib/hooks/useAuthActions";
import Loader from "@/components/ui/loader/Loader";

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider - Centralizes authentication initialization
 * Checks auth once on app mount and handles token validation
 * All routes can then simply check Redux state without making API calls
 */
export default function AuthProvider({ children }: AuthProviderProps) {
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const { checkAuth } = useAuthActions();
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Only run initialization once on app mount
    const initAuth = async () => {
      // Check if we have a token
      const token =
        localStorage.getItem("sb-access-token") ||
        sessionStorage.getItem("sb-access-token") ||
        localStorage.getItem("accessToken") ||
        sessionStorage.getItem("accessToken");

      // If no token, mark as initialized and done
      if (!token) {
        setInitialized(true);
        return;
      }

      // If already authenticated from previous session (e.g., Redux persist), skip check
      if (isAuthenticated) {
        setInitialized(true);
        return;
      }

      // Validate token with backend
      try {
        await checkAuth();
      } catch (error) {
        console.error("Auth initialization failed:", error);
      } finally {
        setInitialized(true);
      }
    };

    initAuth();
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show loading screen only during initial auth check
  // Don't show loader for login/register operations (they handle their own loading state)
  if (!initialized) {
    return <Loader />;
  }

  // Once initialized, render children (routes can check auth state from Redux)
  return <>{children}</>;
}
